import PropTypes from 'prop-types';
import React from 'react';
import slice from 'lodash/slice';
import GenericTable from 'components/Table';
import PdpNotice from 'components/PdpNotice';
import SummaryNumber from 'components/VisualizationStrategies/SummaryNumber';
import phoenixRender, {
  PARTS,
} from 'components/VisualizationStrategies/Chart/phoenixRender';
import {parseDocument, toReactStyle} from 'utilities/DOM';
import Busy from 'components/activity/Busy';
import domToReact from 'utilities/domToReact';
import styles from './Table.css?module';
import get from 'lodash/get';
import Memoize from 'components/Memoize';

const SUMMARY_NUMBER_HEIGHT = 48;
const WARNING_HEIGHT = 24;

export const parseTable = (props) => {
  if (!props.chartRender)
    return {
      tableStyle: null,
      headers: [],
      rows: [],
      columnWidthPercentages: [],
      allowTextToWrap: false,
      warning: null,
    };

  const doc = parseDocument(props.chartRender.html);
  const warningEl = doc.querySelector('.phoenix-warning');
  const warning = warningEl ? warningEl.innerText : null;
  const noDataEl = doc.querySelector('.phoenix-no-data');
  const noData = noDataEl ? noDataEl.innerText : null;
  const tableEl = doc.querySelector('table');
  const tableStyle = tableEl ? toReactStyle(tableEl.style) : null;
  const headers = [...doc.querySelectorAll('th')];
  const trs = slice(doc.querySelectorAll('tr'), 1);
  const rows = trs.map((tr) => [...tr.querySelectorAll('td')]);
  const columnWidthPercentages = headers.map(
    (h) => parseFloat(toReactStyle(h.style).width) / 100
  );

  const allowTextToWrap =
    toReactStyle(get(headers, [0, 'style'], [])).whiteSpace === 'normal';

  return {
    tableStyle,
    headers,
    rows,
    columnWidthPercentages,
    allowTextToWrap,
    warning,
    noData,
  };
};

class Table extends React.Component {
  state = {
    headers: [],
    rows: [],
    columnWidthPercentages: [],
    allowTextToWrap: false,
  };

  handleDrill = (e) => {
    const dataDrill = e.currentTarget.getAttribute('data-drill');
    const {filters} = JSON.parse(dataDrill);
    this.props.onDrill(this.props.cardId, filters);
  };

  numberOfColumns = () => this.props.parsedTable.headers.length;

  numberOfRows = () =>
    Math.min(this.props.maxRows, this.props.parsedTable.rows.length);

  getHeaderAt = (column) => {
    const header = this.props.parsedTable.headers[column];
    return header && domToReact(header.firstChild);
  };

  getHeaderStyleAt = (column) => {
    const header = this.props.parsedTable.headers[column];
    return header && toReactStyle(header.style);
  };

  getColumnWidthPercentages = (column) =>
    this.props.parsedTable.columnWidthPercentages[column];

  getCellStyleAt = (row, column) => {
    const cell = this.props.parsedTable.rows[row][column];
    return cell && toReactStyle(cell.style);
  };

  getCellAt = (row, column) => {
    const cell = this.props.parsedTable.rows[row][column];
    const domCell = domToReact(cell);
    const domCellChildren =
      domCell && domCell.props.children
        ? domCell.props.children.map((dCell) => {
            const hasDrill = dCell.type === 'a' && dCell.props['data-drill'];
            return hasDrill
              ? React.cloneElement(dCell, {
                  ...dCell.props,
                  onClick: this.handleDrill,
                  className: styles.drillTarget,
                })
              : dCell;
          })
        : null;

    return <React.Fragment>{domCellChildren}</React.Fragment>;
  };

  getHeight = () => {
    const {height, showSummary, parsedTable} = this.props;
    const summaryHeight = showSummary ? SUMMARY_NUMBER_HEIGHT : 0;
    const warningHeight = parsedTable.warning ? WARNING_HEIGHT : 0;

    return height - summaryHeight - warningHeight;
  };

  render() {
    const {chartRender, isLoaded, showSummary, width, parsedTable, darkMode} =
      this.props;
    const Component = this.props.component;

    return (
      <div>
        <Busy isLoading={chartRender && !isLoaded}>
          {showSummary && chartRender && chartRender.summary ? (
            <SummaryNumber
              number={chartRender.summary.value}
              label={chartRender.summary.label}
            />
          ) : null}
          {parsedTable.warning ? (
            <div className={styles.warning}>{parsedTable.warning}</div>
          ) : null}
          <div
            className={styles.container}
            style={{maxHeight: this.getHeight()}}
          >
            {parsedTable.noData ? (
              <div className={styles.noData}>{parsedTable.noData}</div>
            ) : (
              <Component
                style={parsedTable.tableStyle}
                width={width}
                height={this.getHeight()}
                className={styles.table}
                numberOfColumns={this.numberOfColumns}
                numberOfRows={this.numberOfRows}
                getHeaderAt={this.getHeaderAt}
                getHeaderStyleAt={this.getHeaderStyleAt}
                getCellAt={this.getCellAt}
                getCellStyleAt={this.getCellStyleAt}
                getColumnWidthPercentages={this.getColumnWidthPercentages}
                allowTextToWrap={parsedTable.allowTextToWrap}
                maxRows={Infinity}
                darkMode={darkMode}
              />
            )}
          </div>
          <PdpNotice chartRender={chartRender} />
        </Busy>
      </div>
    );
  }
}

Table.propTypes = {maxRows: PropTypes.number.isRequired};

Table.defaultProps = {maxRows: Infinity, component: GenericTable};

export default phoenixRender(
  ({
    isResizing,
    filters,
    functionOverrides,
    height,
    phoenixWidth,
    filterGroupIds,
    dataControlFilters,
    dateRangeFilter,
    dateTimeElement,
    pageLayout,
  }) => ({
    isResizing,
    width: phoenixWidth,
    height,
    filters,
    functionOverrides,
    filterGroupIds,
    dataControlFilters,
    scale: 1,
    parts: [PARTS.SUMMARY, PARTS.PDP, PARTS.GRAPH],
    dateRangeFilter,
    dateTimeElement,
    pageLayout,
  })
)((props) => (
  <Memoize chartRender={props.chartRender} memoizeFn={parseTable}>
    {(result) => <Table {...props} parsedTable={result} />}
  </Memoize>
));
